import type { Microsoft365MeetingPayload, Microsoft365MeetingView } from '@dbrain/shared';
import { Field, Icons, TextArea, TextInput, toneOf } from '@dbrain/ui';
import { useTranslation } from 'react-i18next';
import { instantOf, senderName } from '@dbrain/app-core/answer-cards';
import {
  availabilityAt,
  dateTile,
  initialsOf,
  meetingProblems,
  meetingTime,
  recipientChips,
  scheduleCovers,
  timeLine,
  type MeetingFields,
} from '@dbrain/app-core/microsoft365-write';
import { EditablePeople } from './People.js';
import { ScheduleStrip } from './ScheduleStrip.js';

/**
 * The meeting card's body (MsToplantiOnay): the date tile ("EKİM", "31", "Cuma"), the title, "15:30 – 16:00 · 30
 * dakika", the Teams line (or that the account has no Teams, no mockup), the note, the people with the organizer first
 * and whether each is free at the chosen time, and the free/busy strip with "Başka saat seç". Düzenle (no mockup):
 * title, day, start, length, attendees and note; the chips and the strip follow the fields.
 */
export function MeetingBody({
  payload,
  original,
  view,
  decided,
  editing,
  fields,
  onFields,
  onPending,
  onPick,
}: {
  /** what the card shows: the card's payload, the edited version, or the version that was created */
  payload: Microsoft365MeetingPayload;
  /** the card's own payload (its time is offered again after another one is picked) */
  original: Microsoft365MeetingPayload;
  view: Microsoft365MeetingView | null;
  /** the card ended: its header tells the outcome */
  decided: boolean;
  editing: boolean;
  fields: MeetingFields;
  onFields: (fields: MeetingFields) => void;
  /** an address box holds text that was not added */
  onPending?: (box: string, pending: boolean) => void;
  /** "Başka saat seç" while the card waits */
  onPick?: (at: Date) => void;
}) {
  const { t, i18n } = useTranslation();
  const start = instantOf(payload.start) ?? new Date(0);
  const end = instantOf(payload.end) ?? start;
  const time = meetingTime(start, end, payload.timeZone);
  const tile = dateTile(start, payload.timeZone, i18n.language);
  const line = timeLine(time, t);
  const schedule = view?.schedule ?? null;
  const covers = scheduleCovers(schedule, start, end);
  const organizer = view?.organizer ?? null;
  const problems = editing ? meetingProblems(fields, payload.timeZone) : [];
  const date = start.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', weekday: 'long', timeZone: payload.timeZone });
  return (
    <div className="m365w-body" data-testid="m365-write-meeting">
      <div className="m365w-meeting">
        <span className="m365w-tile" aria-hidden="true" data-testid="m365-write-tile">
          <span className="m365w-tile__month">{tile.month}</span>
          <span className="m365w-tile__day">{tile.day}</span>
          <span className="m365w-tile__weekday">{tile.weekday}</span>
        </span>
        <div className="m365w-meeting__main">
          <p className="m365w-meeting__title" data-testid="m365-write-title">
            {payload.subject}
          </p>
          <p className="m365w-line" data-testid="m365-write-time">
            <Icons.time size={15} />
            <span>
              <span className="ui-visually-hidden">{date} · </span>
              <strong>{line.range}</strong> · {line.length}
            </span>
          </p>
          {/* once decided, the header says whether the Teams link came */}
          {decided ? null : (
            <p className="m365w-line" data-testid="m365-write-teams" data-online={payload.online ? 'true' : 'false'}>
              <Icons.video size={15} />
              <span>{t(payload.online ? 'microsoft365.chat.write.meeting.teams' : 'microsoft365.chat.write.meeting.noTeams')}</span>
            </p>
          )}
          {payload.note.trim() ? (
            <p className="m365w-line m365w-line--note" data-testid="m365-write-note">
              <Icons.menu size={15} />
              <span>{payload.note}</span>
            </p>
          ) : null}
        </div>
      </div>
      <ul className="m365w-people" data-testid="m365-write-people">
        {organizer ? (
          <li className="m365w-person" data-testid="m365-write-person" data-role="organizer">
            <span className="m365w-person__avatar ui-avatar--blue" aria-hidden="true">
              {initialsOf(organizer)}
            </span>
            <span className="m365w-person__text">
              <span className="m365w-person__name">{senderName(organizer)}</span>
              <span className="m365w-person__state">{t('microsoft365.chat.write.meeting.organizer')}</span>
            </span>
          </li>
        ) : null}
        {recipientChips(payload.attendees, view?.flags, organizer?.address).map((chip) => {
          const availability = availabilityAt(schedule, chip.address, start, end);
          return (
            <li key={chip.address.toLowerCase()} className="m365w-person" data-testid="m365-write-person" data-availability={availability} data-address={chip.address}>
              <span className={`m365w-person__avatar ui-avatar--${toneOf(chip.address.toLowerCase())}`} aria-hidden="true">
                {chip.initials}
              </span>
              <span className="m365w-person__text">
                <span className="m365w-person__name">
                  {chip.label}
                  {chip.external ? <span className="m365w-chip__external">{t('microsoft365.chat.write.external')}</span> : null}
                </span>
                <span className={`m365w-person__state m365w-person__state--${availability}`}>{t(`microsoft365.chat.write.meeting.availability.${availability}`)}</span>
              </span>
            </li>
          );
        })}
      </ul>
      {schedule ? (
        <ScheduleStrip
          schedule={schedule}
          start={start}
          end={end}
          original={instantOf(original.start) ?? start}
          people={payload.attendees.length + 1}
          timeZone={payload.timeZone}
          {...(onPick ? { onPick } : {})}
        />
      ) : (
        <p className="m365w-quiet" data-testid="m365-write-no-schedule">
          {t('microsoft365.chat.write.meeting.noSchedule')}
        </p>
      )}
      {schedule && !covers ? (
        <p className="m365w-quiet" data-testid="m365-write-not-checked">
          {t('microsoft365.chat.write.meeting.notChecked')}
        </p>
      ) : null}
      {editing ? (
        <div className="m365w-edit" data-testid="m365-write-edit">
          <Field label={t('microsoft365.chat.write.fields.title')} error={problems.includes('title') ? t('microsoft365.chat.write.problems.title') : undefined}>
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={fields.subject}
                maxLength={300}
                onChange={(e) => onFields({ ...fields, subject: e.target.value })}
                data-testid="m365-write-edit-title"
              />
            )}
          </Field>
          <div className="m365w-edit__row">
            <Field label={t('microsoft365.chat.write.fields.date')} error={problems.includes('date') ? t('microsoft365.chat.write.problems.date') : undefined}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  className="ui-input"
                  type="date"
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={fields.date}
                  onChange={(e) => onFields({ ...fields, date: e.target.value })}
                  data-testid="m365-write-edit-date"
                />
              )}
            </Field>
            <Field label={t('microsoft365.chat.write.fields.start')} error={problems.includes('start') ? t('microsoft365.chat.write.problems.start') : undefined}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  className="ui-input"
                  type="time"
                  step={300}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={fields.start}
                  onChange={(e) => onFields({ ...fields, start: e.target.value })}
                  data-testid="m365-write-edit-start"
                />
              )}
            </Field>
            <Field label={t('microsoft365.chat.write.fields.duration')} error={problems.includes('duration') ? t('microsoft365.chat.write.problems.duration') : undefined}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  className="ui-input"
                  type="number"
                  inputMode="numeric"
                  min={15}
                  max={480}
                  step={15}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={Number.isFinite(fields.durationMinutes) ? fields.durationMinutes : ''}
                  onChange={(e) => onFields({ ...fields, durationMinutes: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
                  data-testid="m365-write-edit-duration"
                />
              )}
            </Field>
          </div>
          <EditablePeople
            label={t('microsoft365.chat.write.fields.attendees')}
            people={fields.attendees}
            onChange={(attendees) => onFields({ ...fields, attendees })}
            onPending={onPending}
            flags={view?.flags}
            account={organizer?.address}
            testId="m365-write-edit-attendees"
          />
          <Field label={t('microsoft365.chat.write.fields.note')} error={problems.includes('note') ? t('microsoft365.chat.write.problems.note') : undefined}>
            {({ id, describedBy, invalid }) => (
              <TextArea
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                rows={3}
                value={fields.note}
                onChange={(e) => onFields({ ...fields, note: e.target.value })}
                data-testid="m365-write-edit-note"
              />
            )}
          </Field>
          {problems
            .filter((p) => !['title', 'date', 'start', 'duration', 'note'].includes(p))
            .map((problem) => (
              <p key={problem} className="m365w-edit__error" role="alert" data-testid="m365-write-problem">
                {t(`microsoft365.chat.write.problems.${problem}`)}
              </p>
            ))}
        </div>
      ) : null}
    </div>
  );
}
